// @ts-nocheck

import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, showNotification, useDidUpdate } from 'sonata-react-components';

import { ReportingShareReportModalContext } from '../ReportingShareReportModalContext';

import './reportingShareReportPassword.scss';

const RULES = {
  length: text => text.length > 7 && text.length <= 20,
  lowerCase: text => /^.*[a-z].*/.test(text),
  numberCharacter: text => /^.*[0-9].*/.test(text),
  specialCharacter: text => /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(text),
  upperCase: text => /^.*[A-Z].*/.test(text)
};

function getUpdatedPasswordRules(password = '') {
  return Object.keys(RULES).reduce((acc, ruleKey) => {
    return {
      ...acc,
      [ruleKey]: RULES[ruleKey](password)
    };
  }, {});
}

function getPasswordRuleIconClassName(isPasswordEmpty, isValid) {
  if (isPasswordEmpty) {
    return 'passwordForm__ruleIcon passwordForm__ruleIcon--pending fa-solid fa-dot';
  }
  return `passwordForm__ruleIcon ${isValid ? 'fas' : 'fa-regular'} fa-${isValid ? 'check-circle' : 'times-circle'}`;
}

function ReportingShareReportPassword({ passwordLength = 7 }) {
  const { formatMessage } = useIntl();

  const { currentSharedReportPassword, passwordHasBeenSaved, setContext } = useContext(
    ReportingShareReportModalContext
  );

  const [passwordRules, setPasswordRules] = useState(() => getUpdatedPasswordRules(currentSharedReportPassword));

  useDidUpdate(() => {
    setPasswordRules(getUpdatedPasswordRules(currentSharedReportPassword));
  }, [currentSharedReportPassword]);

  const onSubmit = useCallback(ev => {
    ev && ev.preventDefault();
  }, []);

  const passwordChanged = useCallback(
    newPassword => {
      const newPasswordRules = getUpdatedPasswordRules(newPassword);
      const isValid = Object.values(newPasswordRules).filter(result => !result).length === 0;
      if (passwordHasBeenSaved && isValid) {
        showNotification(
          'warning',
          formatMessage({
            defaultMessage: "You've changed the password, update the link please",
            id: 'reporting.shareReportModal.passwordToast'
          }),
          {
            closable: true,
            icon: 'fal fa-exclamation-triangle'
          }
        );
      }
      setContext(prevContext => ({
        ...prevContext,
        currentSharedReportPassword: newPassword,
        passwordLinkEnabled: false,
        passwordNeedsUpdate: passwordHasBeenSaved && isValid,
        passwordValid: isValid
      }));
      setPasswordRules(newPasswordRules);
    },
    [formatMessage, passwordHasBeenSaved, setContext]
  );

  return (
    <form className="passwordForm" onSubmit={onSubmit}>
      <div className="passwordForm__passwordInputContainer">
        <Input
          className="passwordForm__passwordInput"
          clearable
          customClearIcon="fal fa-trash"
          defaultValue={currentSharedReportPassword}
          id="share_report_modal_password_input"
          label={formatMessage({
            defaultMessage: 'Password',
            id: 'reporting.shareReportPassword.label'
          })}
          maxNumberCharacters={20}
          name="sharedReportModalPasswordInput"
          notFloatingPlaceholder
          onChange={passwordChanged}
          passwordVisibilityIcon
          placeholder={formatMessage({
            defaultMessage: 'Write password',
            id: 'reporting.shareReportPassword.placeholder'
          })}
          shape="squareUI"
          type="password"
          value={currentSharedReportPassword}
        />
      </div>
      <div className="passwordForm__hints">
        <span className="passwordForm__hintsTitle">
          {formatMessage({
            defaultMessage: 'Password must:',
            id: 'reporting.shareReportPassword.passwordText'
          })}
        </span>
        <div className="passwordForm__hintsRules">
          {Object.entries(passwordRules).map(([passwordRule, passwordRuleIsValid]) => (
            <div
              className={`passwordForm__rule passwordForm__rule--${passwordRuleIsValid ? 'valid' : !currentSharedReportPassword ? 'pending' : 'invalid'}`}
              key={`passwordRule_${passwordRule}`}
            >
              <span className={getPasswordRuleIconClassName(!currentSharedReportPassword, passwordRuleIsValid)} />
              <span
                className={`passwordForm__ruleText passwordForm__ruleText--${passwordRuleIsValid ? 'valid' : !currentSharedReportPassword ? 'pending' : 'invalid'}`}
              >
                {formatMessage(
                  {
                    defaultMessage: `${capitalize(passwordRule).replace('length', passwordLength.toString())}`,
                    id: `reporting.shareReportPassword.passwordRules.${passwordRule}`
                  },
                  {
                    length: passwordLength
                  }
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}

ReportingShareReportPassword.propTypes = {
  passwordLength: PropTypes.number
};

export default React.memo(ReportingShareReportPassword);
